import React from "react";
import { Button } from "reactstrap";

/**
 * Catches dynamic import() failures from React.lazy().
 *
 * Fires when a user has an open session and a new build is deployed —
 * their index.html references chunk hashes that no longer exist on the
 * server, so the fetch 404s and React throws a ChunkLoadError. Without
 * this boundary the whole tree unmounts and the user gets a white screen.
 *
 * Recovery: one silent auto-reload (sessionStorage-guarded so we never
 * loop), then a manual prompt if that still fails.
 */

var RELOAD_KEY = "dxo:chunk-reload-attempted";

var isChunkError = function (error) {
    if (!error) return false;

    var name = error.name || "";
    var message = error.message || "";

    return (
        name === "ChunkLoadError" ||
        /Loading chunk [\d]+ failed/i.test(message) ||
        /Loading CSS chunk [\d]+ failed/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message) ||   // Vite
        /error loading dynamically imported module/i.test(message) ||     // Vite
        /Importing a module script failed/i.test(message)                 // Safari
    );
};

class ChunkErrorBoundary extends React.Component {
    constructor(props) {
        super(props);

        this.state = { hasError: false, isChunk: false };
        this.handleReload = this.handleReload.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, isChunk: isChunkError(error) };
    }

    componentDidCatch(error, errorInfo) {
        if (isChunkError(error)) {
            var alreadyTried = sessionStorage.getItem(RELOAD_KEY) === "1";

            if (!alreadyTried) {
                sessionStorage.setItem(RELOAD_KEY, "1");
                window.location.reload();
                return;
            }
        }

        // Non-chunk errors (or a chunk error that survived the reload)
        // still bubble to the console for the browser/Sentry to pick up.
        console.error("ChunkErrorBoundary caught:", error, errorInfo);
    }

    componentDidMount() {
        // A clean mount means the chunk resolved — clear the guard so a
        // future deploy gets its own auto-reload attempt.
        sessionStorage.removeItem(RELOAD_KEY);
    }

    handleReload() {
        sessionStorage.removeItem(RELOAD_KEY);
        window.location.reload();
    }

    render() {
        if (!this.state.hasError) return this.props.children;

        var isChunk = this.state.isChunk;

        return (
            <div className="d-flex flex-column justify-content-center align-items-center text-center px-3" style={{ minHeight: "60vh" }}>
                <div
                    className="d-flex align-items-center justify-content-center rounded-circle mb-4"
                    style={{
                        width: 72,
                        height: 72,
                        background: "linear-gradient(135deg, #1a3a5c 0%, #1e5f8a 45%, #0ab39c 100%)",
                    }}
                >
                    <i className={isChunk ? "ri-refresh-line text-white fs-24" : "ri-error-warning-line text-white fs-24"} />
                </div>

                <h5 className="mb-2">
                    {isChunk ? "A new version is available" : "Something went wrong"}
                </h5>

                <p className="text-muted mb-4" style={{ maxWidth: 420 }}>
                    {isChunk
                        ? "This page was updated while your session was open. Reload to get the latest version."
                        : "This page failed to load. Reloading usually resolves it — if the problem persists, please contact your system administrator."}
                </p>

                <Button color="primary" onClick={this.handleReload}>
                    <i className="ri-refresh-line align-bottom me-1" />
                    Reload page
                </Button>
            </div>
        );
    }
}

export default ChunkErrorBoundary;